import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import moment from 'moment-timezone';

import { PaymentPlanCodes } from '@shared/price-plan/enums';
import { SubscriptionState } from '@shared/recurly/enums';
import { SubscriptionTypes } from '@shared/restaurant/enums';
import { AccountStatus } from '@shared/account/enum';
import { AuthUser } from '@shared/signup/models';
import { IAlertTypes } from '@tablesready/frontend-alerts-bar';

import { AccountAccess, IStatus } from './status.interfaces';
import {
  BillingVisibility,
  FrontendPaidFeatureService,
} from '@tablesready/frontend-paid-feature-alert';

@Injectable({ providedIn: 'root' })
export class StatusService {
  readonly status$: Observable<Record<string, IStatus>>;

  private accountAccess: AccountAccess[] = [];
  private hasNoLocation: () => boolean = () => false;
  private readonly statusSource$ = new BehaviorSubject<Record<string, IStatus>>({});

  constructor(private readonly paidService: FrontendPaidFeatureService) {
    this.status$ = this.statusSource$.asObservable();
  }

  setAccess(access: AccountAccess[]): void {
    this.accountAccess = access;
  }

  /**
   * How to tell that the signed-in session has no location at all. The app reads its token store,
   * which this library cannot import; the auth user alone also lacks one while a token refreshes.
   */
  setNoLocationCheck(check: () => boolean): void {
    this.hasNoLocation = check;
  }

  clearAllStatuses(): void {
    this.statusSource$.next({});
  }

  clearStatusById(statusId: string): void {
    const currentStatus = this.statusSource$.getValue();
    currentStatus[statusId] = undefined;

    this.statusSource$.next(currentStatus);
  }

  add(opts: IStatus): void {
    const currentStatus = this.statusSource$.getValue();
    currentStatus[opts.id] = opts;

    this.statusSource$.next(currentStatus);
  }

  updateStatusBarAlerts(user: AuthUser): void {
    this.clearAllStatuses();
    this.paidService.setUser(user);

    const usage = user.location?.usage;
    const location = user.location;
    const account = user.account;
    const expirationStatus = this.isCardExpired(user);
    const hasInvites = this.accountAccess.filter(accountAccess => Boolean(accountAccess.inviteId) &&
      accountAccess.fromAccountId && user.account?.id !== accountAccess.fromAccountId).length > 0;

    if (hasInvites) {
      this.add({
        type: 'alert-info',
        dismissable: false,
        id: 'invites-available',
        body: 'You have unchecked invites to other accounts. Please check your invites on your',
        link: {
          href: '/profile/accounts',
          name: 'profile page.',
        },
      });
    }

    this.addNoLocationStatus(user);

    if (account?.subscriptionType !== SubscriptionTypes.recurly || !location?.recurly || !account.recurly) {
      return;
    }

    const isPaid = location.recurly.planCode !== PaymentPlanCodes.Free;

    if (location.recurly.planCode === PaymentPlanCodes.Free) {
      const status: IStatus = {
        type: IAlertTypes.warning,
        dismissable: false,
        id: 'free-account-verify-email',
        body: '',
        link: {
          name: 'Resend your verification email.',
          href: '/profile',
        },
      };

      if (user.account.status === AccountStatus.unverified) {
        status.body = '<b>Check your email</b> to verify your account and enable messaging. ';
      }

      if (user.account.status !== AccountStatus.unverified) {
        const linkText = user.account.recurly.trial === undefined ? 'Try a paid plan free for 14 days' : 'Upgrade today';
        const message = user.account.recurly.trial === undefined
          ? `${location.usage.limit - location.usage.total} messages and ${usage?.partiesLimit - usage?.parties} parties remaining this month.`
          : `You’ve lost access to your paid plan features. ${location.usage.limit - location.usage.total} messages and ${usage?.partiesLimit - usage?.parties} parties remaining this month.`;

        const body = this.paidService.getBillingText(
          `<b>Free plan.</b> ${message}`,
          `<b>Free plan.</b> ${message}`,
        );

        status.type = 'alert-dark';
        status.link = {
          name: linkText,
          href: '/settings/location/billing',
          visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
        };

        if (body) {
          status.body = body;
        }
      }

      if (status.body) {
        this.add(status);
      }
    }

    if (user.account.status === AccountStatus.unverified && user.location.recurly.planCode !== PaymentPlanCodes.Free) {
      this.add({
        type: IAlertTypes.warning,
        dismissable: false,
        id: 'free-trial-account-verify-account',
        body: '<b>Check your email</b> to verify your account and enable messaging. ',
        link: {
          name: 'Resend your verification email',
          href: '/profile',
        },
      });
    }

    if (location.recurly.trial && user.location.recurly.planCode !== PaymentPlanCodes.Free) {
      const oneDay = 1000 * 60 * 60 * 24;
      const currentUserPlan = user.account.recurly.subscriptions.find(subscription => subscription.uuid === location.recurly.subscriptionUuid);
      let expiresIn = -1;

      if (currentUserPlan !== undefined) {
        expiresIn = Math.round((currentUserPlan.expiration_date - Date.now()) / oneDay);
      }

      const body = this.paidService.getBillingText(
        `<b>Free trial account.</b> Expires in ${expiresIn} days, with ${location.usage.limit - location.usage.total} messages and ${usage?.partiesLimit - usage?.parties} parties remaining.`,
        `<b>Free trial account.</b> Expires in ${expiresIn} days, with ${location.usage.limit - location.usage.total} messages and ${usage?.partiesLimit - usage?.parties} parties remaining.`,
      );

      if (body) {
        this.add({
          type: 'alert-dark',
          dismissable: false,
          id: 'free-trial-account',
          body,
          link: {
            name: 'Subscribe today',
            href: '/settings/location/billing',
            visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
          },
        });
      }
    }

    if (isPaid && location.recurly.subscription_state === SubscriptionState.canceled as SubscriptionState) {
      const body = this.paidService.getBillingText(
        `Your subscription has been canceled. It will expire <strong>${this.getDaysLeft(location.expiration_date)}</strong>.`,
        `Your subscription has been canceled. It will expire <strong>${this.getDaysLeft(location.expiration_date)}</strong>.`,
      );

      if (body) {
        this.add({
          type: IAlertTypes.warning,
          dismissable: true,
          id: 'your-subscription-has-been-canceled',
          body,
          link: {
            name: 'Reactivate your subscription now.',
            href: '/settings/location/billing',
            visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
          },
        });
      }
    }

    if (location.recurly.subscription_state === SubscriptionState.expired as SubscriptionState) {
      const body = this.paidService.getBillingText(
        '<b>Your subscription has expired.</b>',
        '<b>Your subscription has expired.</b>',
      );

      if (body) {
        this.add({
          type: IAlertTypes.danger,
          dismissable: false,
          id: 'your-subscription-has-expired',
          body,
          link: {
            name: 'Create a new one',
            href: '/settings/location/billing',
            visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
          },
        });
      }
    }

    if (location.recurly.subscription_state === SubscriptionState.paused as SubscriptionState) {
      const body = this.paidService.getBillingText(
        '<b>Your subscription has been paused.</b>',
        '<b>Your subscription has been paused.</b>',
      );

      if (body) {
        this.add({
          type: IAlertTypes.danger,
          dismissable: false,
          id: 'your-subscription-has-been-paused',
          body,
          link: {
            name: 'Reactivate subscription now.',
            href: '/settings/location/billing',
            visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
          },
        });
      }
    }

    if (expirationStatus === SubscriptionState.expired) {
      const body = this.paidService.getBillingText(
        '<b>Your credit card has expired.</b>',
        '<b>Your credit card has expired.</b>',
      );

      if (body) {
        this.add({
          type: IAlertTypes.danger,
          dismissable: true,
          id: 'your-credit-card-has-expired',
          body,
          link: {
            name: 'update your billing information soon.',
            href: '/settings/account/billing',
            visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
          },
        });
      }
    }

    if (expirationStatus === SubscriptionState.expiring) {
      const body = this.paidService.getBillingText(
        `<b>Your credit card will expire soon.</b> Please `,
        `<b>Your credit card will expire soon.</b> Please `,
      );

      if (body) {
        this.add({
          type: IAlertTypes.warning,
          dismissable: true,
          id: 'your-credit-card-will-expire-soon',
          body,
          link: {
            name: 'update your billing information soon',
            href: '/settings/account/billing',
            visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
          },
        });
      }
    }
  }

  getDaysLeft(date) {
    if (!date) {
      return 0;
    }
    const msPerMin = 60000;
    const expires = new Date(date).getTime();
    const now = Date.now();
    const diff = expires - now;
    return moment.duration(diff / msPerMin, 'minutes').humanize(true);
  }

  isCardExpired(user) {
    if (
      user.recurly?.card_expires &&
      user.recurly.subscription_state === SubscriptionState.active
    ) {
      const arr_data = user.recurly.card_expires.split('/');
      const year = arr_data[1];
      const month = arr_data[0];

      if (Number(year) < Number(new Date().getFullYear())) {
        return SubscriptionState.expired;
      }
      if (Number(year) === Number(new Date().getFullYear())) {
        if (Number(month) < Number(new Date().getMonth()) + 1) {
          return SubscriptionState.expired;
        }

        if (Number(month) === Number(new Date().getMonth()) + 1) {
          return SubscriptionState.expiring;
        }
      }
    }

    return 'ok';
  }

  // A signed-in user with no location: removed from the last one, or never added to any
  private addNoLocationStatus(user: AuthUser): void {
    if (!user._id || !this.hasNoLocation()) {
      return;
    }

    this.add({
      type: IAlertTypes.warning,
      dismissable: false,
      id: 'no-location-access',
      body: 'You don’t have access to any location. Ask your account admin to add you, or log out.',
    });
  }
}
